import { type MultiCallTransactionRequest, TransactionStatus } from '@kwenta/sdk/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { selectTransaction } from 'state/app/selectors'
import { selectOneClickTradingSelected } from 'state/futures/common/selectors'
import { selectSnxV3Account } from 'state/futures/snxPerpsV3/selectors'
import { useAppSelector } from 'state/hooks'
import { selectAbstractionWalletEnabled } from 'state/wallet/selectors'
import type { SendUserOpParameters, Transaction } from 'types/accountAbstraction'
import { unserializeTransactionRequest } from 'utils/futures'
import { usePrepareTransaction } from './usePrepareTransaction'

export const usePrefetchTransaction = ({
	stagedTx,
	refetchInterval = 6000,
}: { stagedTx?: MultiCallTransactionRequest<string>; refetchInterval?: number }) => {
	const queryClient = useQueryClient()
	const isOneClickSelected = useAppSelector(selectOneClickTradingSelected)
	const accountId = useAppSelector(selectSnxV3Account)
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)
	const transaction = useAppSelector(selectTransaction)

	const { prepareTransaction } = usePrepareTransaction()

	const isOneClickInProgress = useMemo(
		() =>
			transaction?.type === 'create_session' && transaction?.status !== TransactionStatus.Confirmed,
		[transaction]
	)

	const { data, isLoading } = useQuery({
		queryKey: [stagedTx?.data, isOneClickSelected, isOneClickInProgress, accountId?.toString()],

		queryFn: async () => {
			if (!stagedTx) {
				throw new Error('No staged transaction found')
			}
			const data = unserializeTransactionRequest(stagedTx)
			const request = abstractionWalletEnabled ? { ...data } : await prepareTransaction(data)
			return request
		},
		enabled: !!stagedTx && !isOneClickInProgress,
		refetchInterval,
	})

	const getRequest: () => Promise<Transaction | SendUserOpParameters> = useCallback(async () => {
		if (isLoading) {
			return queryClient.fetchQuery({
				queryKey: [stagedTx?.data, isOneClickSelected, isOneClickInProgress, accountId?.toString()],
			})
		}

		if (!data) {
			throw new Error('No data found')
		}

		return data
	}, [
		queryClient,
		isLoading,
		data,
		isOneClickSelected,
		isOneClickInProgress,
		accountId,
		stagedTx?.data,
	])

	return { getRequest, isFetching: isLoading }
}
